import { Box, Tooltip } from '@mui/material';
import { useState, useRef, useCallback, useMemo, type ReactNode } from 'react';
import { stripHtmlToPlainText } from '../utils/columnUtils';

export const CellTextWithTooltip = ({
  rawText,
  sanitizedHtml,
  children,
  wordWrap = false,
}: {
  rawText?: string;
  sanitizedHtml?: string;
  children?: ReactNode;
  wordWrap?: boolean;
}) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [tooltipTitle, setTooltipTitle] = useState('');

  const plainText = useMemo(() => (rawText ? stripHtmlToPlainText(rawText) : ''), [rawText]);

  const handleMouseEnter = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const textToDisplay = plainText || el.textContent || '';
    if (!textToDisplay) return;

    if (el.scrollWidth - el.clientWidth >= 1) {
      setTooltipTitle(textToDisplay);
      return;
    }

    const allChildren = Array.from(el.querySelectorAll('*'));
    for (const child of allChildren) {
      if (child instanceof HTMLElement && child.scrollWidth - child.clientWidth >= 1) {
        setTooltipTitle(textToDisplay);
        return;
      }
    }

    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      const rangeWidth = range.getBoundingClientRect().width;
      const containerWidth = el.getBoundingClientRect().width;

      if (rangeWidth - containerWidth >= 1) {
        setTooltipTitle(textToDisplay);
        return;
      }
    } catch {
      // Fallback
    }

    try {
      const style = window.getComputedStyle(el);
      const font = `${style.fontWeight || '400'} ${style.fontSize || '14px'} ${style.fontFamily || 'sans-serif'}`;
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (context) {
        context.font = font;
        const textWidth = context.measureText(textToDisplay).width;
        const containerWidth = el.clientWidth || el.getBoundingClientRect().width;

        if (containerWidth > 0 && textWidth - containerWidth >= 1) {
          setTooltipTitle(textToDisplay);
          return;
        }
      }
    } catch {
      // Fallback
    }
  }, [plainText]);

  const handleMouseLeave = useCallback(() => {
    setTooltipTitle('');
  }, []);

  return (
    <Tooltip
      title={tooltipTitle}
      open={tooltipTitle.length > 0}
      placement="top-start"
      arrow
      disableInteractive
    >
      <Box
        component="span"
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          display: 'block',
          whiteSpace: wordWrap ? 'pre-wrap' : 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          overflowWrap: wordWrap ? 'normal' : undefined,
          wordBreak: wordWrap ? 'normal' : undefined,
          width: '100%',
          height: wordWrap ? 'auto' : '100%',
          minWidth: 0,
          '& > span > p, & > span > div, & > span': {
            display: wordWrap ? 'block' : 'inline',
            margin: 0,
            whiteSpace: 'inherit',
            overflow: 'visible',
            height: 'auto',
          },
          '& br': {
            display: wordWrap ? 'block' : 'none',
          },
        }}
      >
        {sanitizedHtml ? (
          <span dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
        ) : (
          (children ?? plainText)
        )}
      </Box>
    </Tooltip>
  );
};
